import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TabsContent } from "@/components/ui/tabs";
import { Loader2, ThumbsUp, ThumbsDown, Share2, Lock, MessageSquare } from "lucide-react";
import { getRecommendations } from "@/lib/api";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

export const TestimonialsPanel = () => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all"); // all | shared | internal

  useEffect(() => {
    getRecommendations()
      .then((d) => setRows(d.recommendations || []))
      .catch(() => { toast.error("Couldn't load testimonials."); setRows([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withComments = (rows || []).filter((r) => (r.comment || "").trim());
  const visible = withComments.filter((r) => {
    if (filter === "shared") return r.value === "up" && r.share_consent;
    if (filter === "internal") return !(r.value === "up" && r.share_consent);
    return true;
  });

  return (
    <TabsContent value="testimonials" className="mt-6 space-y-4" data-testid="testimonials-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading text-xl text-ink">Recommend comments</h2>
        <div className="flex items-center gap-1 text-sm">
          {[["all", "All"], ["shared", "Consented to share"], ["internal", "Internal only"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`testimonial-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full ${filter === k ? "bg-clay text-white" : "text-ink-muted hover:bg-sand-deep"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-ink-muted max-w-2xl">
        Comments left with the "Would you recommend?" prompt. Only comments explicitly marked
        <span className="font-medium"> consented to share</span> may ever be used publicly. No names or contact details are collected.
      </p>

      {rows === null ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-7 w-7 animate-spin text-clay" /></div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-ink-muted flex items-center gap-2"><MessageSquare className="h-4 w-4" /> No comments yet.</p>
      ) : (
        <div className="space-y-3">
          {visible.map((r, i) => {
            const shared = r.value === "up" && r.share_consent;
            return (
              <div key={r.job_id || i} className="bg-white border border-border rounded-2xl p-5" data-testid={`testimonial-${i}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    {r.value === "up" ? (
                      <ThumbsUp className="h-4 w-4 text-forest" />
                    ) : (
                      <ThumbsDown className="h-4 w-4 text-clay" />
                    )}
                    {shared ? (
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-forest bg-forest/10 px-2 py-0.5 rounded-full">
                        <Share2 className="h-3 w-3" /> Consented to share
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-ink-muted bg-sand-deep px-2 py-0.5 rounded-full">
                        <Lock className="h-3 w-3" /> Internal only
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-ink-faint">{fmtDate(r.created_at)}</span>
                </div>
                <p className="mt-3 text-ink-soft leading-relaxed">"{r.comment}"</p>
                <div className="mt-2 text-xs text-ink-faint">
                  {r.course || "—"}{r.country ? ` · ${r.country}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </TabsContent>
  );
};
